// Centralized auth-mode resolution. Stub mode is ONLY allowed when:
//   - Vite is in dev (import.meta.env.DEV === true)  AND
//   - VITE_AUTH_MODE === 'stub' (explicit opt-in)
//
// Any code that wants to honor a stub identity (URL ?stub_email=, fake
// X-User-Email header, dev banner) MUST gate behind isStubModeAllowed().
//
// In production builds (vite build) import.meta.env.DEV === false, so this
// constant evaluates to false at build time and downstream stub branches are
// tree-shaken out of the bundle.

import type { AuthMode } from './types';

export function getDeclaredAuthMode(): AuthMode {
  const raw = (import.meta.env.VITE_AUTH_MODE as string | undefined) ?? 'supabase';
  return raw === 'stub' ? 'stub' : 'supabase';
}

export function isDevBuild(): boolean {
  return import.meta.env.DEV === true;
}

export function isStubModeAllowed(): boolean {
  return isDevBuild() && getDeclaredAuthMode() === 'stub';
}

// ── Modo REVISIÓN solo-preview (bypass de auth gateado por host) ─────────────
//
// PROBLEMA: la preview de Vercel es un build de PRODUCCIÓN (import.meta.env.DEV
// === false), así que el stub dev (isStubModeAllowed) NUNCA aplica ahí, y el
// login real redirige a prod. Para que Jaime pueda navegar el v2 dentro de la
// preview sin salir a prod, habilitamos una SESIÓN MOCK de revisión gateada con
// máxima seguridad por HOST.
//
// DÓNDE SE PERMITE EL BYPASS (entornos de revisión temporales · sin datos reales):
//  - Cualquier preview de Vercel (`*.vercel.app`).
//  - `app2.robotea.io` y `app2.robotea.es` EXACTOS: entorno de revisión v2
//    temporal para que Jaime navegue el rediseño desde el móvil sin login.
//
// POR QUÉ ES SEGURO (no puede aparecer en producción):
//  1) La PRODUCCIÓN real se sirve por DOMINIOS CUSTOM (app.robotea.io /
//     campus.robotea.es / app.robotea.es / robotea.io / www.robotea.io). Esos
//     hosts están en la denylist dura y ABORTAN siempre, antes que cualquier
//     allowlist. app2.* NO es producción: es un entorno de revisión efímero.
//  2) Allowlist explícita y cerrada: solo se habilita el bypass en previews de
//     Vercel o en los hosts `app2.*` EXACTOS. Cualquier otro host → false.
//  3) Si VITE_VERCEL_ENV es legible y vale 'production', ABORTA (el deploy de
//     producción de Vercel lleva VERCEL_ENV=production; se expone como
//     VITE_VERCEL_ENV). Refuerzo: en app2.* no aplica (no es Vercel), pero no
//     lo rompemos.
//  4) La identidad de revisión NO lleva JWT y usa mode='review'. El cliente API
//     (lib/api.ts buildHeaders) SOLO emite cabeceras de auth para
//     mode='supabase' (con JWT) o mode='stub' (gateado a dev). 'review' cae sin
//     cabeceras → el backend prod NUNCA recibe una petición privilegiada: las
//     llamadas a /api/me/* devuelven 401 y la UI muestra estados vacíos honestos.
//     El bypass es 100% cosmético en el frontend; no concede acceso a datos.

/** Hosts de PRODUCCIÓN real donde el modo revisión queda PROHIBIDO sí o sí.
 *  Se evalúa ANTES que la allowlist: ningún dominio de prod puede activar el
 *  bypass jamás, pase lo que pase. */
const PROD_HOSTS_DENYLIST = new Set<string>([
  'app.robotea.io',
  'campus.robotea.es',
  'app.robotea.es',
  'robotea.io',
  'www.robotea.io',
]);

/** Hosts EXACTOS de revisión donde SÍ se permite el bypass (además de las
 *  previews `*.vercel.app`). `app2.*` es un entorno de revisión temporal del
 *  rediseño v2, sin datos reales · pensado para que Jaime lo navegue desde el
 *  móvil sin pasar por el login. NUNCA debe contener un dominio de prod. */
const REVIEW_HOSTS_ALLOWLIST = new Set<string>([
  'app2.robotea.io',
  'app2.robotea.es',
]);

/**
 * True cuando estamos en un entorno de REVISIÓN temporal (preview de Vercel
 * `*.vercel.app` o host `app2.*`) y NUNCA en un dominio de producción real.
 * Único gate que habilita la sesión mock de revisión.
 */
export function isPreviewReviewAllowed(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  const host = window.location.hostname.toLowerCase();

  // (1) Denylist dura: dominios de prod → jamás (gana sobre cualquier allow).
  if (PROD_HOSTS_DENYLIST.has(host)) return false;

  // (3) Refuerzo: si Vercel marca este deploy como production, abortar.
  const vercelEnv = (import.meta.env.VITE_VERCEL_ENV as string | undefined) ?? '';
  if (vercelEnv === 'production') return false;

  // (2) Allowlist: previews de Vercel · o los hosts `app2.*` de revisión v2.
  if (host.endsWith('.vercel.app')) return true;
  if (REVIEW_HOSTS_ALLOWLIST.has(host)) return true;

  return false;
}

// Discord widget config · A3 sprint campus wave 1.
// 2026-05-27 · Jaime cambia al server Robotea (antes Global Online).
// Server Robotea: 1478781515889639599 · canal chat: 1481965554569842800
// · canal anuncios: 1478781518737440770
// Override por entorno con VITE_DISCORD_SERVER_ID + VITE_DISCORD_CHANNEL_ID
// + VITE_DISCORD_ANUNCIOS_CHANNEL_ID + VITE_DISCORD_INVITE_URL.
const DEFAULT_DISCORD_SERVER_ID = '1478781515889639599';
const DEFAULT_DISCORD_CHANNEL_ID = '1481965554569842800';
const DEFAULT_DISCORD_ANUNCIOS_CHANNEL_ID = '1478781518737440770';
const DEFAULT_DISCORD_DUDAS_CHANNEL_ID = '1481965400504930394';
const DEFAULT_DISCORD_INSTALACION_CHANNEL_ID = '1481965444406710394';
const DEFAULT_DISCORD_BITACORA_CHANNEL_ID = '1481965494041968741';
const DEFAULT_DISCORD_PRO_EXCLUSIVO_CHANNEL_ID = '1481965796178792543';
const DEFAULT_DISCORD_INVITE_URL = 'https://discord.gg/robotea';

export function getDiscordServerId(): string {
  const raw = import.meta.env.VITE_DISCORD_SERVER_ID as string | undefined;
  return raw && raw.length > 0 ? raw : DEFAULT_DISCORD_SERVER_ID;
}

export function getDiscordChannelId(): string {
  const raw = import.meta.env.VITE_DISCORD_CHANNEL_ID as string | undefined;
  return raw && raw.length > 0 ? raw : DEFAULT_DISCORD_CHANNEL_ID;
}

export function getDiscordAnunciosChannelId(): string {
  const raw = import.meta.env.VITE_DISCORD_ANUNCIOS_CHANNEL_ID as string | undefined;
  return raw && raw.length > 0 ? raw : DEFAULT_DISCORD_ANUNCIOS_CHANNEL_ID;
}

export function getDiscordDudasChannelId(): string {
  const raw = import.meta.env.VITE_DISCORD_DUDAS_CHANNEL_ID as string | undefined;
  return raw && raw.length > 0 ? raw : DEFAULT_DISCORD_DUDAS_CHANNEL_ID;
}

export function getDiscordInstalacionChannelId(): string {
  const raw = import.meta.env.VITE_DISCORD_INSTALACION_CHANNEL_ID as string | undefined;
  return raw && raw.length > 0 ? raw : DEFAULT_DISCORD_INSTALACION_CHANNEL_ID;
}

export function getDiscordBitacoraChannelId(): string {
  const raw = import.meta.env.VITE_DISCORD_BITACORA_CHANNEL_ID as string | undefined;
  return raw && raw.length > 0 ? raw : DEFAULT_DISCORD_BITACORA_CHANNEL_ID;
}

export function getDiscordProExclusivoChannelId(): string {
  const raw = import.meta.env.VITE_DISCORD_PRO_EXCLUSIVO_CHANNEL_ID as string | undefined;
  return raw && raw.length > 0 ? raw : DEFAULT_DISCORD_PRO_EXCLUSIVO_CHANNEL_ID;
}

export function getDiscordInviteUrl(): string {
  const raw = import.meta.env.VITE_DISCORD_INVITE_URL as string | undefined;
  return raw && raw.length > 0 ? raw : DEFAULT_DISCORD_INVITE_URL;
}
